import React from "react";
import "./DecisionSummary.scss";
import { CompanyAnalytics } from "../../../services/analyticsService";

interface DecisionSummaryData {
  overallSignal: "strong_buy" | "buy" | "hold" | "sell" | "strong_sell";
  signalStrength: number;
  components: {
    technicals: "positive" | "neutral" | "negative";
    valuation: "undervalued" | "fair" | "overvalued" | "neutral";
    sentiment: "positive" | "neutral" | "negative";
  };
  reasoning: string[];
}

interface DecisionSummaryProps {
  companyId: number;
  analytics: CompanyAnalytics | null;
  data?: DecisionSummaryData;
}

const DecisionSummary: React.FC<DecisionSummaryProps> = ({ companyId, analytics, data }) => {
  const getDecisionDataFromAnalytics = (): DecisionSummaryData => {
    if (!analytics?.decision) {
      return {
        overallSignal: "hold",
        signalStrength: 0,
        components: { technicals: "neutral", valuation: "neutral", sentiment: "neutral" },
        reasoning: ["Analytics data not available. Click Refresh to load latest data."]
      };
    }

    const decision = analytics.decision;
    const mapSignal = (signal: string | null): "strong_buy" | "buy" | "hold" | "sell" | "strong_sell" => {
      if (!signal) return "hold";
      const s = signal.toLowerCase();
      if (s === "strong_buy" || s === "strong buy") return "strong_buy";
      if (s === "buy") return "buy";
      if (s === "sell") return "sell";
      if (s === "strong_sell" || s === "strong sell") return "strong_sell";
      return "hold";
    };

    const mapComponentStatus = (signal: string | null): "positive" | "neutral" | "negative" => {
      if (!signal) return "neutral";
      const s = signal.toLowerCase();
      if (s.includes("buy") || s === "bullish" || s === "positive") return "positive";
      if (s.includes("sell") || s === "bearish" || s === "negative") return "negative";
      return "neutral";
    };

    const reasoning: string[] = [];
    if (analytics.technical) {
      if (analytics.technical.ma_signal?.toLowerCase().includes("buy")) {
        reasoning.push("Moving averages showing bullish trend");
      }
      if (analytics.technical.rsi && analytics.technical.rsi < 30) {
        reasoning.push("RSI oversold - potential buying opportunity");
      } else if (analytics.technical.rsi && analytics.technical.rsi > 70) {
        reasoning.push("RSI overbought - consider profit booking");
      }
    }
    if (analytics.volume?.momentum_direction?.toLowerCase() === "up") {
      reasoning.push("Strong volume momentum supporting price movement");
    }
    if (analytics.volume?.delivery_percent && analytics.volume.delivery_percent > 60) {
      reasoning.push("High delivery % indicates institutional interest");
    }
    if (analytics.performance) {
      if (analytics.performance.day_change_percent > 2) {
        reasoning.push("Strong positive momentum today");
      } else if (analytics.performance.day_change_percent < -2) {
        reasoning.push("Negative pressure - caution advised");
      }
    }
    if (reasoning.length === 0) {
      reasoning.push("Mixed signals - monitor closely");
    }

    return {
      overallSignal: mapSignal(decision.overall_signal),
      signalStrength: decision.signal_strength || 50,
      components: {
        technicals: mapComponentStatus(decision.technical_signal),
        valuation: "neutral",
        sentiment: mapComponentStatus(decision.momentum_signal)
      },
      reasoning
    };
  };

  const mockData: DecisionSummaryData = {
    overallSignal: "hold",
    signalStrength: 50,
    components: { technicals: "neutral", valuation: "neutral", sentiment: "neutral" },
    reasoning: ["No data available"]
  };

  const decisionData = data || (analytics ? getDecisionDataFromAnalytics() : mockData);

  const getSignalConfig = (signal: string) => {
    switch (signal) {
      case "strong_buy": return { label: "Strong Buy", color: "is-success", icon: "fa-thumbs-up", bgClass: "strong-buy" };
      case "buy": return { label: "Buy", color: "is-success", icon: "fa-arrow-up", bgClass: "buy" };
      case "hold": return { label: "Hold / Neutral", color: "is-warning", icon: "fa-minus", bgClass: "hold" };
      case "sell": return { label: "Sell", color: "is-danger", icon: "fa-arrow-down", bgClass: "sell" };
      case "strong_sell": return { label: "Strong Sell", color: "is-danger", icon: "fa-thumbs-down", bgClass: "strong-sell" };
      default: return { label: "Neutral", color: "is-info", icon: "fa-question", bgClass: "neutral" };
    }
  };

  const getComponentIcon = (status: string) => {
    switch (status) {
      case "positive":
      case "undervalued":
        return <span className="icon has-text-success"><i className="fas fa-check-circle"></i></span>;
      case "neutral":
      case "fair":
        return <span className="icon has-text-warning"><i className="fas fa-minus-circle"></i></span>;
      case "negative":
      case "overvalued":
        return <span className="icon has-text-danger"><i className="fas fa-times-circle"></i></span>;
      default:
        return null;
    }
  };

  const signalConfig = getSignalConfig(decisionData.overallSignal);

  return (
    <div className={`decision-summary ${signalConfig.bgClass}`}>
      <div className="decision-header">
        <div className="signal-badge">
          <span className="icon is-large">
            <i className={`fas fa-2x ${signalConfig.icon}`}></i>
          </span>
          <h3 className="title is-4 mt-2 mb-2">{signalConfig.label}</h3>
          <p className="subtitle is-6">Overall Signal</p>
        </div>
        <div className="signal-strength">
          <p className="heading mb-2">Confidence</p>
          <progress className={`progress ${signalConfig.color}`} value={decisionData.signalStrength} max="100">{decisionData.signalStrength}%</progress>
          <p className="has-text-weight-bold">{decisionData.signalStrength}%</p>
        </div>
      </div>
      <div className="decision-body">
        <p className="heading mb-3">Components Contributing</p>
        <div className="components-list">
          <div className="component-item">
            {getComponentIcon(decisionData.components.technicals)}
            <span className="component-label">Technicals: <strong className="text-capitalize">{decisionData.components.technicals}</strong></span>
          </div>
          <div className="component-item">
            {getComponentIcon(decisionData.components.valuation)}
            <span className="component-label">Valuation: <strong className="text-capitalize">{decisionData.components.valuation}</strong></span>
          </div>
          <div className="component-item">
            {getComponentIcon(decisionData.components.sentiment)}
            <span className="component-label">Sentiment: <strong className="text-capitalize">{decisionData.components.sentiment}</strong></span>
          </div>
        </div>
      </div>
      <div className="decision-footer">
        <p className="heading mb-3">Key Reasons</p>
        <ul className="reasoning-list">
          {decisionData.reasoning.map((reason, index) => (
            <li key={index}>
              <span className="icon is-small mr-1"><i className="fas fa-chevron-right"></i></span>
              {reason}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DecisionSummary;
